import { useState } from "react";
import { format } from "date-fns";
import type { DoctorBooking } from "@/features/appointments/domain/entities/Appointment";
import { useLazyGetDoctorBookingsQuery } from "@/features/appointments/infrastructure/api/appointmentsApi";

/** Bookings in these states no longer occupy the doctor's time. */
const RELEASED_STATUS_SUFFIXES = [
  "_CANCELLED",
  "_DISCARDED",
  "_DROPPED",
  "_NOT_REQUIRED",
];

const isReleased = (status: string) =>
  RELEASED_STATUS_SUFFIXES.some((suffix) => status?.endsWith(suffix));

export type SlotRequest = {
  doctorIds: string[];
  start: Date;
  end: Date;
  /** The assessment being (re)scheduled; its own booking is not a clash. */
  excludeAssessmentId?: string;
};

/**
 * Returns the first active booking that shares a doctor with the request and
 * overlaps its time range, or null when every selected doctor is free.
 */
export const findDoctorConflict = (
  bookings: DoctorBooking[],
  { doctorIds, start, end, excludeAssessmentId }: SlotRequest,
): DoctorBooking | null => {
  const requested = new Set(doctorIds);
  return (
    bookings.find((booking) => {
      if (booking.assessmentId === excludeAssessmentId) return false;
      if (isReleased(booking.status)) return false;
      if (!booking.doctorIds.some((id) => requested.has(id))) return false;

      const bookedStart = new Date(booking.scheduledStartAt).getTime();
      const bookedEnd = new Date(booking.scheduledEndAt).getTime();
      if (Number.isNaN(bookedStart)) return false;
      // A booking without a valid end blocks its exact start time only.
      const effectiveEnd = Number.isNaN(bookedEnd) ? bookedStart + 1 : bookedEnd;

      return bookedStart < end.getTime() && start.getTime() < effectiveEnd;
    }) ?? null
  );
};

/**
 * Checks the selected doctors' existing appointments before scheduling.
 * Resolves to true when the slot is free. If the lookup itself fails, it
 * resolves to true and leaves the final decision to the API.
 */
export const useDoctorAvailabilityCheck = () => {
  const [fetchBookings] = useLazyGetDoctorBookingsQuery();
  const [isChecking, setIsChecking] = useState(false);

  const isSlotAvailable = async (request: SlotRequest): Promise<boolean> => {
    const dates = Array.from(
      new Set([
        format(request.start, "yyyy-MM-dd"),
        format(request.end, "yyyy-MM-dd"),
      ]),
    );

    setIsChecking(true);
    try {
      const results = await Promise.all(
        dates.map((schedule_date) =>
          fetchBookings({
            schedule_date,
            assigned_doctor_user_ids: request.doctorIds,
          }).unwrap(),
        ),
      );
      return findDoctorConflict(results.flat(), request) === null;
    } catch (err) {
      console.error("[scheduling] doctor availability check failed", err);
      return true;
    } finally {
      setIsChecking(false);
    }
  };

  return { isSlotAvailable, isChecking };
};
